import { REMOTE_SPEAKER_PREFIX } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Container,
  CopyButton,
  Drawer,
  Group,
  Highlight,
  Text,
  TextInput,
  Title,
} from "@mantine/core"
import { useMediaQuery } from "@mantine/hooks"
import { modals } from "@mantine/modals"
import {
  IconAlertTriangle,
  IconArrowBackUp,
  IconCopy,
  IconFileText,
  IconPencil,
  IconPlayerPlay,
  IconSearch,
  IconSparkles,
} from "@tabler/icons-react"
import { Link } from "@tanstack/react-router"
import { useWindowVirtualizer } from "@tanstack/react-virtual"
import { type ReactNode, useCallback, useMemo, useRef, useState } from "react"
import { EmptyState } from "../../../../components/EmptyState"
import { useMeetingPlayer } from "../../../../components/MeetingSummary/MeetingPlayerContext"
import { MeetingTabs } from "../../../../components/MeetingTabs"
import { LineListSkeleton } from "../../../../components/PageSkeletons"
import { MiniPlayer } from "../../../../components/Player/MiniPlayer"
import { SearchInput } from "../../../../components/SearchInput"
import { WhoSpokePanel } from "../../../../components/WhoSpokePanel"
import { languageDisplayName } from "../../../../lib/language-display"
import { identityInkColor, identityTextColor, resolveSpeakerColor } from "../../../../lib/speaker-colors"
import { speakerInitials } from "../../../../lib/speaker-format"
import { isUnidentifiedSpeaker } from "../../../../lib/speaker-mapping"
import { type ISpeakerVM, speakerDisplayLabel } from "../../../../lib/speaker-vm"
import { formatMeetingOffset, isShortTranscriptTurn, type TTranscriptBatchEdge, transcriptLineEdge } from "../helpers"
import type { IViewProps } from "./interfaces"

import classes from "./styles.module.css"

interface ILineRowProps {
  index: number
  /** Raw diarisation prefix stored on the line — drives the unidentified-row styling. */
  speaker: string
  /** Resolved, user-facing label for the prefix (unified "Speaker N" / stripped name). */
  displaySpeaker: string
  /** Avatar letters. Roster initials when we have them, otherwise derived from the label. */
  initials: string
  text: string
  search: string
  saving: boolean
  /** Identity hue mixed toward the text anchor so the name stays readable. */
  nameColor: string | undefined
  /** Raw identity hue for the avatar fill and the batch wash. */
  speakerColor: string | undefined
  /** Where this line sits in a run of the same speaker. */
  edge: TTranscriptBatchEdge
  /** Offset label, present when the line carries a start. */
  timestamp?: string
  /** Seconds from the meeting start. Absent when the line has no timing. */
  startSeconds?: number
  /** Play from here. When omitted, the row uses the meeting player context. */
  onPlayFrom?: (seconds: number) => void
  onCommit: (index: number, oldText: string, newText: string) => void
}

/**
 * One transcript line. Clicking the words edits them. Hover, keyboard focus,
 * and the first tap on a touch screen reveal Play, Edit, and Copy.
 */
function TranscriptLineRow({
  index,
  speaker,
  displaySpeaker,
  initials,
  text,
  search,
  saving,
  nameColor,
  speakerColor,
  edge,
  timestamp,
  startSeconds,
  onPlayFrom,
  onCommit,
}: ILineRowProps): React.JSX.Element {
  const { i18n } = useLingui()
  const player = useMeetingPlayer()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(text)
  const [hot, setHot] = useState(false)
  const [revealed, setRevealed] = useState(false)

  const handleCommit = useCallback((): void => {
    const trimmed = draft.trim()
    setEditing(false)
    if (!trimmed || trimmed === text) {
      setDraft(text)
      return
    }
    onCommit(index, text, trimmed)
  }, [draft, text, index, onCommit])

  const beginEdit = useCallback((): void => {
    setDraft(text)
    setEditing(true)
  }, [text])

  const seek = useCallback(
    (seconds: number): void => {
      if (onPlayFrom) {
        onPlayFrom(seconds)
        return
      }
      player.play(seconds)
    },
    [onPlayFrom, player],
  )

  const onTurnClick = useCallback(
    (event: React.MouseEvent<HTMLElement>): void => {
      if (editing) return
      const target = event.target
      if (target instanceof Element && target.closest("button, a, input, textarea")) return
      const coarse = typeof window.matchMedia === "function" && window.matchMedia("(hover: none)").matches
      if (coarse && !revealed) {
        setRevealed(true)
        return
      }
      beginEdit()
    },
    [beginEdit, editing, revealed],
  )

  const onBlur = useCallback((event: React.FocusEvent<HTMLElement>): void => {
    const next = event.relatedTarget
    if (next instanceof Node && event.currentTarget.contains(next)) return
    setHot(false)
  }, [])

  const unidentified = isUnidentifiedSpeaker(speaker)
  const continued = edge === "mid" || edge === "end"
  const compact = edge === "only" && !editing && isShortTranscriptTurn(text)
  const showHead = !continued
  const actionsOpen = !editing && (hot || revealed)

  const body = editing ? (
    <TextInput
      className={classes.edit}
      value={draft}
      onChange={(e) => setDraft(e.currentTarget.value)}
      onBlur={handleCommit}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === "Enter") handleCommit()
        if (e.key === "Escape") {
          setDraft(text)
          setEditing(false)
        }
      }}
      disabled={saving}
      size="xs"
      autoFocus
    />
  ) : (
    <div className={classes.body}>{search ? <Highlight highlight={search}>{text}</Highlight> : text}</div>
  )

  return (
    <Box
      className={`${classes.turn}${compact ? ` ${classes.short}` : ""}${continued ? ` ${classes.continued}` : ""}`}
      data-testid="transcript-turn"
      data-edge={edge}
      data-editing={editing ? "true" : undefined}
      data-actions={actionsOpen ? "open" : undefined}
      data-unidentified={unidentified ? "true" : undefined}
      style={speakerColor ? ({ "--turn-speaker": speakerColor } as React.CSSProperties) : undefined}
      onClick={onTurnClick}
      onMouseEnter={() => setHot(true)}
      onMouseLeave={(event) => {
        const active = document.activeElement
        if (active instanceof Node && event.currentTarget.contains(active)) return
        setHot(false)
      }}
      onFocus={() => setHot(true)}
      onBlur={onBlur}
    >
      {showHead ? (
        <span
          className={`${classes.avatar}${speakerColor ? "" : ` ${classes.avatarPlain}`}`}
          style={speakerColor ? { background: speakerColor, color: identityInkColor(speakerColor) } : undefined}
          aria-hidden="true"
        >
          {initials}
        </span>
      ) : null}
      {showHead ? (
        <span className={classes.name} style={nameColor ? { color: nameColor } : undefined}>
          {displaySpeaker}
        </span>
      ) : null}
      <div className={classes.tools}>
        {timestamp && startSeconds != null ? (
          <button
            type="button"
            className={classes.time}
            aria-label={i18n._(msg`Play from ${timestamp}`)}
            onClick={() => seek(startSeconds)}
          >
            {timestamp}
          </button>
        ) : null}
        <div className={classes.actions}>
          {startSeconds != null ? (
            <ActionIcon
              className={classes.action}
              variant="subtle"
              size="sm"
              aria-label={i18n._(msg`Play from here`)}
              onClick={() => seek(startSeconds)}
            >
              <IconPlayerPlay size={14} />
            </ActionIcon>
          ) : null}
          <ActionIcon
            className={classes.action}
            variant="subtle"
            size="sm"
            aria-label={i18n._(msg`Edit`)}
            onClick={beginEdit}
          >
            <IconPencil size={14} />
          </ActionIcon>
          <CopyButton value={text}>
            {({ copy }) => (
              <ActionIcon
                className={classes.action}
                variant="subtle"
                size="sm"
                aria-label={i18n._(msg`Copy`)}
                onClick={copy}
              >
                <IconCopy size={14} />
              </ActionIcon>
            )}
          </CopyButton>
        </div>
      </div>
      {editing ? <div className={classes.body}>{body}</div> : body}
    </Box>
  )
}

/**
 * Full transcript page — pure presentation. The smart route wrapper owns the
 * transcript query and the line-save mutation; this view renders the states,
 * the controlled search, and the editable lines.
 */
export const TranscriptView = ({
  meetingId,
  lines,
  speakers,
  onEditSpeaker,
  onConfirmSuggestion,
  onRejectSuggestion,
  suggestionPending,
  speakerColorMap,
  search,
  onSearchChange,
  isLoading,
  error,
  savingIndex,
  onSaveLine,
  onRetry,
  cleaned,
  restoring,
  onRestore,
  speakersSlot = "inline",
  layout = "page",
  lineCount,
  heading,
  showMeetingTabs = false,
  onMeetingTab,
  player,
  onPlayFrom,
  narrow,
}: IViewProps): React.JSX.Element => {
  const { i18n } = useLingui()
  const embedded = layout === "embedded"
  const totalLines = lineCount ?? lines.length
  // 62em is the Mantine `md` breakpoint. Below it the player docks.
  const queriedNarrow = useMediaQuery("(max-width: 61.99em)", false, { getInitialValueInEffect: false })
  const isNarrow = narrow ?? queriedNarrow
  const [sheetOpen, setSheetOpen] = useState(false)
  const showDock = !embedded && isNarrow && Boolean(player)

  const speakerLookup = useMemo(() => {
    const byLabel = new Map<string, ISpeakerVM>()
    const byName = new Map<string, ISpeakerVM>()
    for (const vm of speakers) {
      if (vm.label !== null && !byLabel.has(vm.label)) byLabel.set(vm.label, vm)
      if (!byName.has(vm.displayName)) byName.set(vm.displayName, vm)
    }
    return { byLabel, byName }
  }, [speakers])

  const resolveLineSpeaker = useCallback(
    (prefix: string): string => {
      const vm = speakerLookup.byLabel.get(prefix) ?? speakerLookup.byName.get(prefix)
      if (vm) return speakerDisplayLabel(vm, i18n)
      return prefix.startsWith(REMOTE_SPEAKER_PREFIX) ? prefix.slice(REMOTE_SPEAKER_PREFIX.length) : prefix
    },
    [speakerLookup, i18n],
  )

  const listRef = useRef<HTMLDivElement>(null)
  const virtualizer = useWindowVirtualizer({
    count: lines.length,
    estimateSize: () => 72,
    overscan: 12,
    scrollMargin: listRef.current?.offsetTop ?? 0,
    getItemKey: (index) => lines[index]?.index ?? index,
  })

  const confirmRestore = useCallback((): void => {
    modals.openConfirmModal({
      title: i18n._(msg`Restore the original transcript?`),
      children: (
        <Text size="sm">
          <Trans>
            This replaces the cleaned-up transcript with the original recording text — filler words and all. The summary
            was generated from the cleaned version and won't change. You can clean it up again by reprocessing the
            meeting.
          </Trans>
        </Text>
      ),
      labels: { confirm: i18n._(msg`Restore original`), cancel: i18n._(msg`Cancel`) },
      onConfirm: onRestore,
    })
  }, [i18n, onRestore])

  const openSheet = useCallback((): void => {
    setSheetOpen(true)
  }, [])

  const closeSheet = useCallback((): void => {
    setSheetOpen(false)
  }, [])

  const header = embedded ? null : (
    <>
      <Link to="/meetings/$id" params={{ id: meetingId }} className={classes.back}>
        &larr; <Trans>Back to meeting</Trans>
      </Link>
      <Title order={1} className={classes.pageTitle}>
        {heading && heading.length > 0 ? heading : <Trans>Transcript</Trans>}
      </Title>
      {showMeetingTabs ? (
        <MeetingTabs meetingId={meetingId} current="transcript" onChange={onMeetingTab ?? (() => undefined)} />
      ) : null}
      {!showDock && player ? <div className={classes.stickyPlayer}>{player}</div> : null}
    </>
  )

  const dock = showDock ? (
    <>
      <Drawer
        opened={sheetOpen}
        onClose={closeSheet}
        position="bottom"
        keepMounted
        size="auto"
        title={<Trans>Recording</Trans>}
        classNames={{ body: classes.playerSheet }}
      >
        {player}
      </Drawer>
      <MiniPlayer onExpand={openSheet} />
    </>
  ) : null

  const frame = (body: ReactNode): React.JSX.Element => (
    <Container
      size="lg"
      className={classes.page}
      data-dock={showDock ? "true" : undefined}
      data-testid="transcript-view"
    >
      {header}
      {body}
      {dock}
    </Container>
  )

  if (isLoading) {
    const skeleton = <LineListSkeleton rows={10} />
    if (embedded) {
      return (
        <section className={classes.card} data-testid="transcript-view">
          <div className={classes.cardBody}>{skeleton}</div>
        </section>
      )
    }
    return frame(skeleton)
  }
  if (error) {
    const empty = (
      <EmptyState
        tone="danger"
        icon={<IconAlertTriangle />}
        title={<Trans>Couldn't load the transcript</Trans>}
        description={error}
        action={
          onRetry ? (
            <Button variant="default" onClick={onRetry}>
              <Trans>Try again</Trans>
            </Button>
          ) : undefined
        }
      />
    )
    if (embedded) {
      return (
        <section className={classes.card} data-testid="transcript-view">
          <div className={classes.cardBody}>{empty}</div>
        </section>
      )
    }
    return frame(empty)
  }

  const cleanedBanner = cleaned ? (
    <Alert
      color="info"
      variant="light"
      mb="md"
      icon={<IconSparkles size={18} />}
      classNames={{
        root: classes.cleanedNoticeRoot,
        body: classes.cleanedNoticeBody,
        message: classes.cleanedNoticeMessage,
      }}
    >
      <div className={classes.cleanedNoticeFrame} data-testid="cleaned-notice">
        <div className={classes.cleanedNotice}>
          <Text size="sm" className={classes.cleanedNoticeText}>
            <Trans>This transcript was cleaned up by AI (filler words removed). The original is kept.</Trans>
          </Text>
          <Button
            className={classes.cleanedNoticeAction}
            size="compact-sm"
            variant="default"
            leftSection={<IconArrowBackUp size={14} />}
            loading={restoring}
            onClick={confirmRestore}
          >
            <Trans>Restore original</Trans>
          </Button>
        </div>
      </div>
    </Alert>
  ) : null

  const speakerPanel =
    speakersSlot === "inline" && speakers.length > 0 ? (
      <WhoSpokePanel
        meetingId={meetingId}
        speakers={speakers}
        onEditSpeaker={(speakerVm) => onEditSpeaker(speakerVm.label ?? speakerVm.displayName)}
        onConfirmSuggestion={onConfirmSuggestion}
        onRejectSuggestion={onRejectSuggestion}
        suggestionPending={suggestionPending}
      />
    ) : null

  const list =
    lines.length === 0 ? (
      search ? (
        <EmptyState
          tone="neutral"
          icon={<IconSearch />}
          title={<Trans>No lines match your search</Trans>}
          description={<Trans>Try a different word or speaker name.</Trans>}
        />
      ) : (
        <EmptyState
          tone="neutral"
          icon={<IconFileText />}
          title={<Trans>No transcript yet</Trans>}
          description={<Trans>This meeting doesn't have a transcript.</Trans>}
        />
      )
    ) : (
      <div ref={listRef} style={{ position: "relative", width: "100%", height: `${virtualizer.getTotalSize()}px` }}>
        {virtualizer.getVirtualItems().map((vItem) => {
          const line = lines[vItem.index]
          if (!line) return null
          const prev = vItem.index > 0 ? lines[vItem.index - 1] : undefined
          const showLangDivider = Boolean(
            line.language && prev?.language && line.language.toLowerCase() !== prev.language.toLowerCase(),
          )
          const langName = line.language ? languageDisplayName(line.language, i18n.locale) : ""
          const langWhen = line.start != null ? formatMeetingOffset(line.start) : null
          const edge = transcriptLineEdge(lines, vItem.index, resolveLineSpeaker)
          const flush = edge === "mid" || edge === "end"
          const vm = speakerLookup.byLabel.get(line.speaker) ?? speakerLookup.byName.get(line.speaker)
          const displaySpeaker = resolveLineSpeaker(line.speaker)
          const hue = vm?.resolvedColor ?? resolveSpeakerColor(line.speaker, speakerColorMap)
          const timestamp = line.start != null ? formatMeetingOffset(line.start) : undefined
          return (
            <div
              key={line.index}
              data-testid="transcript-line"
              data-index={vItem.index}
              data-edge={edge}
              ref={virtualizer.measureElement}
              className={flush ? classes.slotFlush : classes.slot}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${vItem.start - virtualizer.options.scrollMargin}px)`,
              }}
            >
              {showLangDivider ? (
                <div className={classes.languageDivider} data-testid="transcript-language-divider">
                  {langWhen ? (
                    <Trans>
                      {langName} from {langWhen}
                    </Trans>
                  ) : (
                    langName
                  )}
                </div>
              ) : null}
              <TranscriptLineRow
                index={line.index}
                speaker={line.speaker}
                displaySpeaker={displaySpeaker}
                initials={vm?.initials ?? speakerInitials(displaySpeaker, vm?.displayIndex)}
                text={line.text}
                search={search}
                saving={savingIndex === line.index}
                nameColor={identityTextColor(hue)}
                speakerColor={hue}
                edge={edge}
                timestamp={timestamp}
                startSeconds={line.start}
                onPlayFrom={onPlayFrom}
                onCommit={onSaveLine}
              />
            </div>
          )
        })}
      </div>
    )

  const linesBlock = (
    <>
      {cleanedBanner}
      {speakerPanel}
      {list}
    </>
  )

  if (embedded) {
    return (
      <section className={classes.card} data-testid="transcript-view">
        <header className={classes.cardHead}>
          <div className={classes.cardTitle}>
            <span className={classes.overline}>
              <Trans>Transcript</Trans>
            </span>
            <span className={classes.lineCount}>
              <Trans>{totalLines} lines</Trans>
            </span>
          </div>
          <SearchInput value={search} onChange={onSearchChange} placeholder={i18n._(msg`Search transcript...`)} />
        </header>
        <div className={classes.cardBody}>{linesBlock}</div>
      </section>
    )
  }

  return frame(
    <>
      <Group justify="flex-end" mb="md">
        <SearchInput value={search} onChange={onSearchChange} placeholder={i18n._(msg`Search transcript...`)} />
      </Group>
      <Text size="xs" c="dimmed" mb="xs">
        <Trans>Click any line to correct it. Word corrections are automatically added to your custom vocabulary.</Trans>
      </Text>
      {linesBlock}
    </>,
  )
}
